/**
 * ObjectView — the full object page of the farm twin explorer
 * (design §3.2/§3.3, replaces the inline accordion of bug B10).
 *
 * Foundry-style object view: header (icon, display name, type label),
 * Eigenschaften (generic schema-driven property grid), Verknüpfungen
 * (ontology-walked relations), Belege (provenance documents), Verlauf
 * (events + audit). Works for ANY entity type — types without a lens
 * (reached by traversing relations) render data-tolerantly without
 * ontology labels.
 */
import React from 'react';
import { ArrowLeft, RotateCcw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SourceStatusBadge } from '@hofsync/design-system';
import type { EntitySourceDoc, LenseConfig, LenseField, ReverseRelation } from '../../../services/twinService';
import type { SourceRowDto } from '../../../services/sourcesService';
import {
    useEntitiesByIds,
    useEntityRelations,
    useEntitySourceDocs,
    useEntityEventsBulk,
    useEntityHistory,
    useEntityTransitionsComplete,
} from '../../../hooks/useTwin';
import { useSourceSystems } from '../../../hooks/useSourceSystems';
import { useEntitySources } from '../../../hooks/useSources';
import { AuthContext } from '../../../contexts/AuthContext';
import { useInlineEntityEdit } from '../../../hooks/useInlineEntityEdit';
import { isEditableRow, mergeFieldValueLabels } from './editValue';
import { resolveEntityTitle } from '../displayName';
import { prettifyKey, formatDateString } from '../formatValue';
import PropertyGrid from './PropertyGrid';
import RelationsPanel from './RelationsPanel';
import EntityBelege from './EntityBelege';
import EntityMengen from './EntityMengen';
import HistoryPanel from './HistoryPanel';
import AsOfNav from './AsOfNav';
import SourcesPanel, { type EditorIntent } from './sources/SourcesPanel';
import NeedsAssignmentCard from './sources/NeedsAssignmentCard';
import {
    reconstructAttributesAsOf,
    deriveAsOfStops,
    attributeHistoryMeta,
    newestInstantMs,
    stopBeforeLastChange,
    normalizeAsOf,
} from './asof';
import './ObjectView.css';

export interface ObjectViewRow {
    id: string;
    name?: string;
    properties: Record<string, unknown>;
}

export interface ObjectViewProps {
    entityType: string;
    entityId: string;
    /** Row from the already-fetched list page; fetched by id when absent. */
    row?: ObjectViewRow | null;
    /** Merged ontology schema from the lens (absent for headless types). */
    fields?: LenseField[];
    /** feed_display.title template of the lens, for the header title. */
    titleTemplate?: string | null;
    /** German labels for enum-ish values (field id → value → label). */
    valueLabels?: Record<string, Record<string, string>>;
    icon?: React.ReactNode;
    /** German type label (lens name / ontology entity name). */
    typeLabel?: string;
    /** Live twin mode — relations/docs/events fetch only when true. */
    live: boolean;
    backLabel: string;
    onBack: () => void;
    onNavigate: (entityType: string, entityId: string) => void;
    onShowAllRelation?: (rel: ReverseRelation) => void;
    hasLensForType?: (entityType: string) => boolean;
    onPreviewDocument?: (docId: string) => void;
    /**
     * #1913 C — the active as-of instant (``?asof=``), or null/undefined for
     * the live view. When set, the property grid renders the entity's state
     * reconstructed at that instant.
     */
    asOf?: string | null;
    /**
     * #1913 C — set (instant) or clear (null) the as-of instant. When absent,
     * the as-of "Zeitreise" navigation is not offered (e.g. demo/mock views).
     */
    onAsOfChange?: (iso: string | null) => void;
    /** FR-018.7 — the object's lens, for the Sources section's vocabulary while it loads. */
    lens?: LenseConfig;
    /** FR-018.7 — open the mapping editor (hosted by the page). */
    onOpenSourcesEditor?: (intent: EditorIntent) => void;
    /** FR-018.7 — open the shared-group panel for a `shared` row (hosted by the page). */
    onOpenSourcesGroup?: (row: SourceRowDto) => void;
    /** FR-018.7 §8 — open the vocabulary-term page; a crop cycle's `crop` value links to it. */
    onOpenTerm?: (vocabulary: 'crop', term: string) => void;
}

const noop = () => {};

export const ObjectView: React.FC<ObjectViewProps> = ({
    entityType,
    entityId,
    row,
    fields,
    titleTemplate,
    valueLabels,
    icon,
    typeLabel,
    live,
    backLabel,
    onBack,
    onNavigate,
    onShowAllRelation,
    hasLensForType,
    onPreviewDocument,
    asOf = null,
    onAsOfChange,
    lens,
    onOpenSourcesEditor,
    onOpenSourcesGroup,
    onOpenTerm,
}) => {
    const { t } = useTranslation();
    // #3385 — one session-lifetime fetch of the source-system registry, shared
    // by every object view. Falls back to the bundled table while in flight and
    // on error, so a registry outage never blanks the badges.
    const sourceSystems = useSourceSystems();
    // FR-018.7 §4 — the Sources read (deduped with SourcesPanel's by React Query) tells
    // whether this object is a parked stub: pill + subtitle + the needs-assignment card.
    const { data: sourcesData } = useEntitySources(entityType, entityId, live);
    const stubSources =
        live && sourcesData?.object?.is_stub && sourcesData.object.entity_id === entityId ? sourcesData : null;
    // Deep link beyond the fetched page (or relation navigation): fetch by id.
    const needsFetch = live && !row;
    const { data: byIdData, isLoading: rowLoading } = useEntitiesByIds(
        needsFetch ? entityType : undefined,
        needsFetch ? [entityId] : undefined,
    );
    const fetchedRow: ObjectViewRow | null = React.useMemo(() => {
        const raw = byIdData?.entities?.[0];
        if (!raw) return null;
        return {
            id: String(raw.entity_id ?? entityId),
            name: typeof raw.name === 'string' ? raw.name : undefined,
            properties: raw as Record<string, unknown>,
        };
    }, [byIdData, entityId]);
    const effectiveRow = row ?? fetchedRow;

    const { data: relations, isLoading: relationsLoading, isError: relationsError } = useEntityRelations(
        live ? entityType : undefined,
        live ? entityId : undefined,
    );
    const { data: docs, isLoading: docsLoading, isError: docsError } = useEntitySourceDocs(
        live ? entityType : undefined,
        live ? entityId : undefined,
    );
    const { data: eventsByEntity, isLoading: eventsLoading } = useEntityEventsBulk(
        live ? entityType : undefined,
        live ? [entityId] : undefined,
    );
    const events = eventsByEntity?.[entityId] ?? [];
    // #2791 — the linked-document index the property grid needs to tell a
    // previewable document id apart from a source-system token. Derived from
    // the Belege read that already happened above: no extra request.
    const docsById = React.useMemo(() => {
        const map = new Map<string, EntitySourceDoc>();
        for (const doc of docs ?? []) map.set(doc.document_id, doc);
        return map;
    }, [docs]);

    // As-of navigation (#1913 C). The LIVE view derives the stepper stops from
    // HistoryPanel's page-1 read (React Query dedupes the identical key — so
    // opening an object view costs ONE history request and never eagerly walks
    // the full history). The COMPLETE paged series is fetched LAZILY, only when
    // the user is actually time-travelling AND page 1 didn't already contain
    // everything (`has_more`), so parity on a >1000-transition entity holds
    // without a per-open cost (round-3 #2). Gated by `live` like the sibling
    // reads so a demo/mock object view never hits the B-read API with mock ids.
    const page1 = useEntityHistory(live ? entityType : undefined, live ? entityId : undefined);
    const page1Transitions = React.useMemo(() => page1.data?.transitions ?? [], [page1.data]);
    const page1HasMore = page1.data?.has_more ?? false;
    // Accepted boundary (round-3 #3): the LIVE stepper stops are derived from page
    // 1 only, so on a >1000-transition entity an attribute whose changes ALL fall
    // past row 1000 contributes no stop until the first historical interaction
    // triggers the full paged walk below — which then self-corrects the stops. The
    // lightweight `page1.data.total` count is already served if a future stop-count
    // hint is wanted; no prod entity is anywhere near 1000 transitions today, so we
    // accept the page-1 derivation rather than eager-walk every open (round-3 #2).
    // The GLOBAL newest recorded instant. History is server-ordered newest-first,
    // so page 1's max IS the global max even on a >1000-row entity — the full
    // paged walk can never raise it. `newestInstantMs` reduces (NOT a spread that
    // would overflow on a 5M-row series, round-3 #5) and is order-independent.
    const newestMs = React.useMemo(() => newestInstantMs(page1Transitions), [page1Transitions]);

    const effectiveAsOf = normalizeAsOf(asOf);
    const historicalIntent = live && effectiveAsOf !== null;
    // At/after the newest recorded change reverts nothing == the live view.
    // Decide this DURING RENDER from page 1 (round-3 #4/#7a): a deep link there
    // then (a) never forces the full paged walk, and (b) never paints one
    // historical frame before a post-paint effect can strip `?asof=`.
    const atOrAfterNewest =
        historicalIntent && effectiveAsOf !== null && newestMs !== null && Date.parse(effectiveAsOf) >= newestMs;
    // Lazy: only walk every page when time-travelling a genuinely-historical
    // instant AND page 1 wasn't the whole series. When `has_more` is false, page
    // 1 IS the complete series; at/after newest we already know it's live.
    const needComplete = historicalIntent && page1HasMore && !atOrAfterNewest;
    const complete = useEntityTransitionsComplete(
        needComplete ? entityType : undefined,
        needComplete ? entityId : undefined,
    );

    const transitions = needComplete && complete.data ? complete.data : page1Transitions;
    // Distinguish ERROR from empty: a failed /history read must NOT look like a
    // zero-transition entity (which would strip ?asof= and show live-as-
    // historical — round-3 #1).
    const seriesLoading = page1.isLoading || (needComplete && complete.isLoading);
    const seriesError = page1.isError || (needComplete && complete.isError);
    const seriesReady = !seriesLoading && !seriesError;
    const asOfStops = React.useMemo(() => deriveAsOfStops(transitions), [transitions]);

    // The instant actually RENDERED as historical: only once the series is
    // ready and there are stops to travel through. While loading or on error
    // we do NOT fall back to live (that would paint live-as-historical or lose
    // the deep link — round-3 #1/#5). `historicalIntent` is the user's URL
    // intent regardless of load state.
    const activeAsOf =
        historicalIntent && !atOrAfterNewest && seriesReady && asOfStops.length > 0 ? effectiveAsOf : null;
    const isHistorical = activeAsOf != null;
    const asOfError = historicalIntent && seriesError;
    const asOfLoading = historicalIntent && !seriesError && seriesLoading;

    // HOF-626 — nullable on purpose: this view is rendered bare by its suites and
    // reachable from surfaces that mount no AuthProvider; no context => no seat
    // (fail closed: protected fields show no badge).
    // HOF-696 D20 — the seat decides, not the legacy account type: the backend lets
    // any seat with `twin.write.protected` (owner, farm_manager) write these fields.
    const auth = React.useContext(AuthContext);
    const canWriteProtected = Boolean(auth?.userProfile?.active_farm?.permissions?.includes('twin.write.protected'));
    const canEditHere = live && !isHistorical && !historicalIntent;
    // HOF-696 D20 — enum fields show their lens `value_labels` (App-Zugang:
    // automatisch / freigegeben / gesperrt) in the grid and in the select editor.
    const gridValueLabels = React.useMemo(() => mergeFieldValueLabels(fields, valueLabels), [fields, valueLabels]);
    const { pending, save, orphanedError, reportOrphanedError, dismissOrphanedError } = useInlineEntityEdit({
        entityType,
        entityId,
        enabled: canEditHere,
        properties: effectiveRow?.properties,
        fields,
    });
    const editing = React.useMemo(
        () =>
            canEditHere
                ? {
                      canEdit: ({ field }: { key: string; field?: LenseField }) => isEditableRow(field, { canWriteProtected }),
                      onSave: save,
                      pending,
                      onOrphanedError: reportOrphanedError,
                  }
                : undefined,
        [canEditHere, canWriteProtected, save, pending, reportOrphanedError],
    );

    const displayProperties = React.useMemo(
        () => reconstructAttributesAsOf(effectiveRow?.properties ?? {}, transitions, activeAsOf),
        [effectiveRow, transitions, activeAsOf],
    );
    // Single group-by pass (round-3 #9): canReveal from each attribute's latest
    // transition, provenance from the transition governing `activeAsOf`. The
    // helper also gates the reveal affordance to LIVE mode (round-3 #6).
    const historyByAttribute = React.useMemo(
        () => attributeHistoryMeta(transitions, activeAsOf),
        [transitions, activeAsOf],
    );
    // The per-attribute affordance jumps the as-of to just before that
    // attribute last changed — revealing its previous value in place.
    const handleAttributeHistory = React.useCallback(
        (attribute: string) => {
            const stop = stopBeforeLastChange(transitions, attribute);
            if (stop && onAsOfChange) onAsOfChange(stop.at);
        },
        [transitions, onAsOfChange],
    );
    // Self-heal a bad/stale `?asof=` back to live — but NEVER on error (that
    // would silently drop a valid deep link when /history is merely down —
    // round-3 #1) and never while still loading (round-3 #5). Cleared when the
    // value is empty/invalid, when the entity genuinely has no transitions, or
    // when the instant is at/after the newest change (== live — round-3 #6).
    React.useEffect(() => {
        if (!live || !onAsOfChange || asOf == null) return; // param absent
        const norm = normalizeAsOf(asOf);
        if (norm === null) {
            onAsOfChange(null); // empty / unparseable / calendar-overflow / zone-less
            return;
        }
        if (seriesError || seriesLoading) return; // don't strip on failure or mid-load
        if (transitions.length === 0) {
            onAsOfChange(null); // genuine 200-empty
            return;
        }
        // `newestMs` is the reduce-derived global newest (round-3 #5 — no spread).
        if (newestMs !== null && Date.parse(norm) >= newestMs) onAsOfChange(null); // >= newest change == live
    }, [live, onAsOfChange, asOf, seriesError, seriesLoading, transitions, newestMs]);

    if (!effectiveRow && rowLoading) {
        return (
            <div className="object-view">
                <button type="button" className="object-view__back" onClick={onBack}>
                    <ArrowLeft size={14} />
                    {backLabel}
                </button>
                <p className="object-view__loading">Wird geladen…</p>
            </div>
        );
    }
    if (!effectiveRow) {
        return (
            <div className="object-view">
                <button type="button" className="object-view__back" onClick={onBack}>
                    <ArrowLeft size={14} />
                    {backLabel}
                </button>
                <p className="object-view__missing">Dieses Objekt wurde nicht gefunden.</p>
            </div>
        );
    }

    // In historical mode the header must agree with the grid: title from the
    // RECONSTRUCTED properties (a title-driving attribute that changed shows
    // its as-of value, not the live one — codex round-2 #5). ``resolveEntityTitle``
    // merges ``row.name`` OVER ``properties.name``, so sync the row-level
    // ``name`` with the reconstructed value. When ``name`` IS a tracked
    // attribute but was absent at T (e.g. the pristine stop), fall to the
    // template's own empty→neutral chain rather than the LIVE name the entity
    // did not yet have (round-3 #4); only keep the live name when ``name`` is a
    // display-only field the recorder never tracks.
    const reconstructedName = displayProperties.name;
    const historicalName =
        typeof reconstructedName === 'string'
            ? reconstructedName
            : 'name' in effectiveRow.properties
              ? undefined
              : effectiveRow.name;
    const titleRow = isHistorical
        ? { ...effectiveRow, name: historicalName, properties: displayProperties }
        : effectiveRow;
    const { title, isFallback } = resolveEntityTitle(titleRow, titleTemplate ?? undefined);
    // Headless types: the ontology's German label from the relations
    // response beats a prettified English key ("Lieferung" not "Delivery Event").
    const label = typeLabel || relations?.entity_label || prettifyKey(entityType);
    // FK attributes render in the relations panel, not the grid.
    const suppressKeys = [
        ...(relations?.forward.map((f) => f.field) ?? []),
        ...((fields ?? []).filter((f) => f.ref).map((f) => f.id)),
    ];
    // "Stand": the live `updated_at` only. In historical mode the AsOfNav is
    // the SINGLE Stand readout — suppressing the header line here avoids both a
    // contradictory second date (round-2 #4) and the pristine-anchor day-shift
    // that a header date at the oldest stop would show (round-3 #7).
    const updatedAt = effectiveRow.properties.updated_at;
    // Suppress the live Stand whenever the URL INTENDS a historical view — not
    // only once `isHistorical` resolves — so a deep-linked as-of that is still
    // loading or errored never flashes the live date over the historical
    // placeholder (round-3 #7b). Finding #8 (decided wontfix): the live date
    // stays UTC via `formatDateString`, matching AsOfNav's UTC stop labels — one
    // date convention across the whole view, not viewer-local.
    const updatedStr =
        !isHistorical && !historicalIntent && typeof updatedAt === 'string' && updatedAt
            ? formatDateString(updatedAt)
            : null;
    // The relations/documents/events side panels have no as-of parameter, so in
    // historical mode they still show the LIVE data (documented MVP boundary:
    // as-of = property reconstruction only, #1913 finding 1). Flag them with a
    // "Live-Daten" badge so a time-travelling reader never mistakes the live side
    // panels for the historical instant framed by the property grid.
    const liveBadge = isHistorical ? (
        <span
            className="object-view__live-badge"
            title="Diese Angaben zeigen immer den aktuellen Stand – nicht den gewählten historischen Zeitpunkt."
        >
            Live-Daten
        </span>
    ) : null;

    const titleHeading = (
        <h2 className={`object-view__title${isFallback ? ' object-view__title--fallback' : ''}`}>{title}</h2>
    );
    const stubSystemId = stubSources?.object.stub?.source_system ?? null;
    const stubFirstSeen = stubSources?.object.stub?.first_seen ?? null;
    const stubSubtitle = stubSources
        ? t('farm.sources.stub.subtitle', 'aus {{system}} · zuerst gesehen am {{date}}', {
              system:
                  (stubSystemId && sourceSystems.find((s) => s.id === stubSystemId)?.display_name) ||
                  stubSystemId ||
                  '–',
              date: stubFirstSeen ? formatDateString(stubFirstSeen) : '–',
          })
        : null;

    return (
        <div className="object-view">
            <button type="button" className="object-view__back" onClick={onBack}>
                <ArrowLeft size={14} />
                {backLabel}
            </button>

            <header className="object-view__header">
                {icon && <span className="object-view__icon">{icon}</span>}
                <div className="object-view__heading">
                    {stubSources ? (
                        <div className="object-view__title-row">
                            {titleHeading}
                            <SourceStatusBadge variant="unassigned">
                                {t('farm.sources.badge.unassigned', 'nicht zugeordnet')}
                            </SourceStatusBadge>
                        </div>
                    ) : (
                        titleHeading
                    )}
                    <span className="object-view__type">
                        {label}
                        {stubSources && <span className="object-view__stub-origin"> · {stubSubtitle}</span>}
                        {updatedStr && <span className="object-view__updated"> · Stand: {updatedStr}</span>}
                    </span>
                </div>
            </header>

            <div className="object-view__grid">
                <div className="object-view__main">
                    {stubSources && (
                        <NeedsAssignmentCard
                            key={entityId}
                            sources={stubSources}
                            onOpenEditor={onOpenSourcesEditor ?? noop}
                        />
                    )}
                    <section className="object-view__section object-view__section--properties">
                        <h3 className="object-view__section-title">Eigenschaften</h3>
                        {orphanedError && (
                            <p className="object-view__edit-error" role="alert">
                                <span>{orphanedError}</span>
                                <button type="button" onClick={dismissOrphanedError} aria-label={t('common.close')}>
                                    ×
                                </button>
                            </p>
                        )}
                        {live && onAsOfChange && asOfStops.length > 0 && (
                            // Reflect the URL's historical intent even while the
                            // full series loads or has errored, so the stepper
                            // position matches the deep link.
                            <AsOfNav stops={asOfStops} asOf={effectiveAsOf} onChange={onAsOfChange} />
                        )}
                        {asOfError ? (
                            // A failed /history read must be honest — never paint
                            // live values as if they were the historical state
                            // (round-3 #1). The deep-linked ?asof= is preserved, but
                            // the user still needs an escape hatch back to live: when
                            // page 1 itself errored the stepper (which carries "Jetzt")
                            // could not be derived, so surface an explicit reset here
                            // (round-3 finding 2).
                            <div className="object-view__asof-error">
                                <p className="object-view__asof-error-text">
                                    Verlauf konnte nicht geladen werden – der historische Stand ist nicht verfügbar.
                                </p>
                                {onAsOfChange && (
                                    <button
                                        type="button"
                                        className="object-view__asof-reset"
                                        onClick={() => onAsOfChange(null)}
                                    >
                                        <RotateCcw size={13} />
                                        Jetzt anzeigen
                                    </button>
                                )}
                            </div>
                        ) : asOfLoading ? (
                            // Don't flash live values before the historical fold is
                            // ready for a deep-linked as-of (round-3 #5).
                            <p className="object-view__loading">Historischer Stand wird geladen…</p>
                        ) : (
                            <PropertyGrid
                                properties={displayProperties}
                                fields={fields}
                                suppressKeys={suppressKeys}
                                valueLabels={gridValueLabels}
                                asOf={onAsOfChange ? activeAsOf : null}
                                // #2791 — NOT gated on `onAsOfChange`: the source
                                // badges are a live feature now, so a view that
                                // wires no as-of navigation still gets them. The
                                // reveal affordance below stays gated (it needs a
                                // stepper to jump).
                                historyByAttribute={historyByAttribute}
                                onAttributeHistory={onAsOfChange ? handleAttributeHistory : undefined}
                                onPreviewDocument={onPreviewDocument}
                                docsById={docsById}
                                // #3385 — the badge table comes from the server
                                // registry, fetched HERE rather than in the grid:
                                // PropertyGrid is rendered bare by its own suites
                                // and must stay free of a QueryClient dependency.
                                // Until the fetch lands this is the bundled five,
                                // so the first paint is never badge-less.
                                sourceSystems={sourceSystems}
                                onOpenTerm={entityType === 'crop_cycle' ? onOpenTerm : undefined}
                                editing={editing}
                            />
                        )}
                    </section>

                    {live && (
                        // FR-018.7 — "Herkunft", below Eigenschaften in the left column (spec §2).
                        <section className="object-view__section object-view__section--sources">
                            <h3 className="object-view__section-title">{t('farm.sources.title', 'Herkunft')}</h3>
                            <SourcesPanel
                                entityType={entityType}
                                entityId={entityId}
                                lens={lens}
                                displayName={isFallback ? undefined : title}
                                onOpenEditor={onOpenSourcesEditor ?? noop}
                                onOpenGroup={onOpenSourcesGroup ?? noop}
                                onNavigate={onNavigate}
                                onPreviewDocument={onPreviewDocument}
                            />
                        </section>
                    )}
                </div>

                <div className="object-view__side">
                    <section className="object-view__section">
                        <h3 className="object-view__section-title">Verknüpfungen{liveBadge}</h3>
                        <RelationsPanel
                            relations={relations}
                            isLoading={live && relationsLoading}
                            isError={relationsError}
                            onNavigate={onNavigate}
                            onShowAll={onShowAllRelation}
                            hasLensForType={hasLensForType}
                        />
                    </section>

                    <section className="object-view__section">
                        <h3 className="object-view__section-title">
                            Belege
                            {docs && docs.length > 0 ? ` (${docs.length})` : ''}
                            {liveBadge}
                        </h3>
                        <EntityBelege
                            entityType={entityType}
                            entityId={entityId}
                            live={live}
                            docs={docs}
                            isLoading={live && docsLoading}
                            isError={docsError}
                            onPreview={onPreviewDocument}
                        />
                    </section>

                    {/* FR-017 #4c — renders nothing until this item has a quantity link. */}
                    <EntityMengen
                        entityType={entityType}
                        entityId={entityId}
                        live={live}
                        docs={docs}
                        onPreview={onPreviewDocument}
                        badge={liveBadge}
                    />
                </div>

                <section className="object-view__section object-view__section--history">
                    <h3 className="object-view__section-title">Verlauf{liveBadge}</h3>
                    <HistoryPanel
                        events={events}
                        eventsLoading={live && eventsLoading}
                        entityType={entityType}
                        entityId={entityId}
                        live={live}
                        fields={fields}
                        valueLabels={gridValueLabels}
                        sourceDocs={docs}
                        onPreviewDocument={onPreviewDocument}
                    />
                </section>
            </div>
        </div>
    );
};

export default ObjectView;
